import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MeasurementAdvanceConfigI } from '..'
import { ADVANCE_CONFIG_OPTION } from '../../../_common/constant'
import { ExperimentDetailsI } from '../../../_config/types'
import Icon from '../../../components/icons'
import Text from '../../../elements/Typo/Text'
import { useOutsideClick } from '../../../_common/hook/useOutsideClick'
import { trackEvent } from '../../../_common/functions'

interface AdvanceConfigurationI {
    selectedConfig: any
    setSelectedConfig: React.Dispatch<React.SetStateAction<{ label: string; value: string } | undefined>>
    experimentDetails: ExperimentDetailsI
    nMatches: number | string
    cliper: number | string
    MeasurementAdvanceConfig: MeasurementAdvanceConfigI
    getCStatus: (x: any) => string
}

const advanceConfigOptions = [
    { label: ADVANCE_CONFIG_OPTION.SYNTHETIC_CONTROL.label, value: ADVANCE_CONFIG_OPTION.SYNTHETIC_CONTROL.value },
    { label: ADVANCE_CONFIG_OPTION.DID.label, value: ADVANCE_CONFIG_OPTION.DID.value },
]

const AdvanceConfigurationButton: React.FC<AdvanceConfigurationI> = ({ selectedConfig, setSelectedConfig, experimentDetails, nMatches, cliper, MeasurementAdvanceConfig, getCStatus }) => {
    const { t } = useTranslation()
    const [showDropdown, setShowDropdown] = useState<boolean>(false)
    const experimentTypeTitle = experimentDetails?.experiment_type?.title
    const isPostTreatmentComparison = experimentTypeTitle === 'Post-Treatment Comparison'
    const showDesignMapping = experimentTypeTitle !== 'RCT' && !isPostTreatmentComparison && !experimentDetails?.is_measurement_flow
    //const { resetInsightFilter } = useInsightFilterReport()
    const dropdownRef = useRef<HTMLDivElement>(null)

    useOutsideClick(dropdownRef, () => {
        setShowDropdown(false)
    })

    /* useEffect(() => {
        resetInsightFilter()
    }, []) */

    useEffect(() => {
        console.log('Current selectedConfig:', selectedConfig, 'checkbox:', selectedConfig?.value == advanceConfigOptions[1].value)
    }, [selectedConfig])

    return (
        <div className="relative" ref={dropdownRef}>
            {!isPostTreatmentComparison && (
                <button
                    type="button"
                    onClick={() => {
                        const next = !showDropdown
                        if (next) {
                            trackEvent('experiment_details_advance_config_opened', { experiment_literal: experimentDetails?.experiment_literal, selected_kpi: selectedConfig?.label })
                        }
                        setShowDropdown((prev) => !prev)
                    }}
                    className="flex gap-[0.33rem] items-center"
                >
                    <div className="h-[1.182rem] w-[1.182rem] flex items-center justify-center bg-primary rounded-[1.045rem] border-20">
                        <Icon name="settings" size={'0.55rem'} color="#000" />
                    </div>
                    <Text
                        className="!text-[0.591rem] !font-semibold"
                        style={{
                            fontFamily: 'Poppins, sans-serif',
                            lineHeight: '100%',
                        }}
                    >
                        {t('Advanced Configuration')}
                    </Text>
                </button>
            )}

            {showDropdown && (
                <div className="absolute shadow-lg border border-slate-300 overflow-hidden dropdown top-full mt-1 rounded-lg bg-white z-10" style={{ right: -4, width: '14rem', height: 'auto' }}>
                    <Text size="13" className="font-[600] text-center py-2 hidden">
                        {t('Choose Methodology for Insights')}
                    </Text>
                    <div className="bg-[#F0F0F0] h-[2.083rem]  grid-cols-5 hidden">
                        <button type="button" className="text-[0.5rem] font-[600] leading-none col-span-2">
                            {advanceConfigOptions[0].label}
                        </button>
                        <div className="flex items-center justify-center">
                            <input
                                type="checkbox"
                                className="toggle !border-0 text-[#222222] toggle-primary"
                                checked={selectedConfig?.value == advanceConfigOptions[1].value}
                                onChange={(e) => {
                                    e.stopPropagation()
                                    e.preventDefault()
                                    const newMethodology = e.target.checked ? advanceConfigOptions[1] : advanceConfigOptions[0]
                                    trackEvent('experiment_details_methodology_changed', { experiment_literal: experimentDetails?.experiment_literal, methodology: newMethodology.value, previous_methodology: selectedConfig?.value })
                                    if (e.target.checked) {
                                        setSelectedConfig(advanceConfigOptions[1])
                                    } else {
                                        setSelectedConfig(advanceConfigOptions[0])
                                    }
                                    return false
                                }}
                            />
                        </div>
                        <button type="button" className="text-[0.5rem] font-[600] leading-none col-span-2">
                            {advanceConfigOptions[1].label}
                        </button>
                    </div>

                    <div>
                        <Text size="13" className="font-[600] text-center py-2">
                            {t('Configuration Used')}
                        </Text>
                    </div>

                    {showDesignMapping && (
                        <div className="px-4 py-2 text-[0.667rem]  bg-gray-100 m-2 rounded-sm mb-3">
                            <Text size="15" className="font-semibold text-center text-slate-600">
                                {t('Design Mapping Stage')}
                            </Text>
                            <div className="flex flex-col gap-2 my-3 mb-6">
                                <div className="flex gap-2 items-center">
                                    <span className="font-['Poppins'] font-normal text-[12px] leading-[100%] tracking-[0px]">{t('N Matches')}</span>
                                    <span className="border-t flex-grow"></span>
                                    <span className="leading-none">{experimentDetails?.experiment_metadatas?.design_config?.n_match ? experimentDetails?.experiment_metadatas?.design_config?.n_match?.join(', ') : nMatches}</span>
                                </div>

                                <div className="flex gap-2 items-center">
                                    <span className="font-['Poppins'] font-normal text-[12px] leading-[100%] tracking-[0px]">{t('Caliper')}</span>
                                    <span className="border-t flex-grow"></span>
                                    <span className="leading-none">{experimentDetails?.experiment_metadatas?.design_config?.caliper ? experimentDetails?.experiment_metadatas?.design_config?.caliper?.join(', ') : cliper}</span>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="px-4 py-2 text-[0.667rem]  bg-gray-100 m-2 rounded-sm">
                        <Text size="15" className="font-semibold text-center text-slate-600">
                            {t('Measurement Step')}
                        </Text>
                        <div>
                            <div className="flex flex-col gap-2 my-3">
                                {Object.entries(MeasurementAdvanceConfig).map(([key, prop]) => {
                                    return (
                                        <div key={key} className="flex gap-2 items-center">
                                            <span className="font-['Poppins'] font-normal text-[12px] leading-[100%] tracking-[0px]">{prop.label}</span>
                                            <span className="border-t flex-grow"></span>
                                            {getCStatus(prop)}
                                            {prop.type === 'numericStepper' && <span className="leading-none">{prop.value as number}</span>}
                                        </div>
                                    )
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default AdvanceConfigurationButton
